/* Client session manager — NO supabase-js. Tokens come from our backend's */
/* GoTrue proxy (/auth/*); we persist them in localStorage and refresh before */
/* expiry. This is what lets the web drop the supabase-js client entirely */
/* (clean-arch target: supabase lives only in apps/api). */

import { BACKEND_URL } from './config';

export interface SessionUser {
  id: string;
  email?: string | null;
  [k: string]: unknown;
}
export interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number; /* epoch ms */
  user: SessionUser;
}

const K_ACCESS = 'naia.auth.access';
const K_REFRESH = 'naia.auth.refresh';
const K_EXPIRES = 'naia.auth.expires';
const K_USER = 'naia.auth.user';

export type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'PASSWORD_RECOVERY';
type Listener = (event: AuthEvent, session: Session | null) => void;
const listeners = new Set<Listener>();

function emit(event: AuthEvent, session: Session | null) {
  for (const l of listeners) l(event, session);
}

export function onSessionChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function read(): Session | null {
  try {
    const access_token = localStorage.getItem(K_ACCESS);
    const refresh_token = localStorage.getItem(K_REFRESH);
    const expires = localStorage.getItem(K_EXPIRES);
    const userRaw = localStorage.getItem(K_USER);
    if (!access_token || !refresh_token || !expires || !userRaw) return null;
    return { access_token, refresh_token, expires_at: Number(expires), user: JSON.parse(userRaw) as SessionUser };
  } catch {
    return null;
  }
}

function write(s: Session | null) {
  try {
    if (!s) {
      [K_ACCESS, K_REFRESH, K_EXPIRES, K_USER].forEach((k) => localStorage.removeItem(k));
      return;
    }
    localStorage.setItem(K_ACCESS, s.access_token);
    localStorage.setItem(K_REFRESH, s.refresh_token);
    localStorage.setItem(K_EXPIRES, String(s.expires_at));
    localStorage.setItem(K_USER, JSON.stringify(s.user));
  } catch {
    /* storage unavailable (private mode) — session stays in-memory only */
  }
}

/** Normalize a GoTrue token response into our Session shape. */
function toSession(body: Record<string, unknown>): Session | null {
  const access_token = body.access_token as string | undefined;
  const refresh_token = body.refresh_token as string | undefined;
  const user = body.user as SessionUser | undefined;
  if (!access_token || !refresh_token || !user) return null;
  const expiresAt = body.expires_at as number | undefined;
  const expiresIn = body.expires_in as number | undefined;
  const expires_at = expiresAt ? expiresAt * 1000 : Date.now() + (expiresIn ?? 3600) * 1000;
  return { access_token, refresh_token, expires_at, user };
}

type Json = Record<string, unknown>;

async function post(path: string, body: unknown, bearer?: string, method = 'POST'): Promise<{ ok: boolean; status: number; json: Json }> {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let json: Json = {};
  try {
    json = text ? (JSON.parse(text) as Json) : {};
  } catch {
    json = { error: text };
  }
  return { ok: res.ok, status: res.status, json };
}

function errMsg(json: Json): string {
  return (json.msg || json.error_description || json.error || json.message || 'Authentication failed') as string;
}

export async function signIn(email: string, password: string): Promise<{ error: string | null }> {
  const { ok, json } = await post('/auth/signin', { email, password });
  if (!ok) return { error: errMsg(json) };
  const s = toSession(json);
  if (!s) return { error: 'Invalid auth response' };
  write(s);
  emit('SIGNED_IN', s);
  return { error: null };
}

export async function signUp(email: string, password: string): Promise<{ error: string | null }> {
  const { ok, json } = await post('/auth/signup', { email, password });
  if (!ok) return { error: errMsg(json) };
  const s = toSession(json);
  if (s) {
    write(s);
    emit('SIGNED_IN', s);
  }
  return { error: null };
}

export async function recover(email: string, redirectTo?: string): Promise<{ error: string | null }> {
  const { ok, json } = await post('/auth/recover', { email, redirectTo });
  return { error: ok ? null : errMsg(json) };
}

export async function exchangeRecoveryToken(token: string): Promise<boolean> {
  const { ok, json } = await post('/auth/recover/exchange', { token });
  if (!ok) return false;
  const s = toSession(json);
  if (!s) return false;
  write(s);
  emit('PASSWORD_RECOVERY', s);
  return true;
}

export async function updatePassword(password: string): Promise<{ error: string | null }> {
  const token = await getAccessToken();
  if (!token) return { error: 'Not authenticated' };
  const { ok, json } = await post('/auth/user', { password }, token, 'PUT');
  return { error: ok ? null : errMsg(json) };
}

export async function signOut(): Promise<void> {
  const s = read();
  if (s) void post('/auth/logout', {}, s.access_token).catch(() => undefined);
  write(null);
  emit('SIGNED_OUT', null);
}

let refreshing: Promise<Session | null> | null = null;
async function refresh(s: Session): Promise<Session | null> {
  if (!refreshing) {
    refreshing = (async () => {
      const { ok, json } = await post('/auth/refresh', { refresh_token: s.refresh_token });
      const next = ok ? toSession(json) : null;
      if (next) {
        write(next);
        emit('TOKEN_REFRESHED', next);
      } else {
        write(null);
        emit('SIGNED_OUT', null);
      }
      return next;
    })().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

export function getSession(): Session | null {
  return read();
}

/** Returns a valid access token, refreshing if it expires within 60s. */
export async function getAccessToken(): Promise<string | null> {
  const s = read();
  if (!s) return null;
  if (s.expires_at - Date.now() > 60_000) return s.access_token;
  const next = await refresh(s);
  return next?.access_token ?? null;
}

/* Recovery / magic-link flow: GoTrue redirects with tokens in the URL hash */
/* (#access_token=…&refresh_token=…&type=recovery). Capture them on load, store */
/* the session, and surface PASSWORD_RECOVERY so the reset page can react. */
export function initFromUrl(): void {
  if (typeof window === 'undefined' || !window.location.hash) return;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return;
  const expires_in = Number(params.get('expires_in') ?? 3600);
  const type = params.get('type');
  /* We don't have the user object from the hash; decode the JWT `sub`/`email`. */
  let user: SessionUser = { id: '' };
  try {
    const payload = JSON.parse(atob(access_token.split('.')[1]!));
    user = { id: payload.sub, email: payload.email };
  } catch {
    /* leave minimal */
  }
  const s: Session = { access_token, refresh_token, expires_at: Date.now() + expires_in * 1000, user };
  write(s);
  history.replaceState(null, '', window.location.pathname + window.location.search);
  emit(type === 'recovery' ? 'PASSWORD_RECOVERY' : 'SIGNED_IN', s);
}
